import { getAccessToken } from "../utils/browserStorage";

import { toast } from "react-toastify";
/**
 * @typedef RequestBody
 * @type {object}
 * @property {string} method - Request method [GET|POST|PUT|DELETE]
 * @property {string} mode - CORS enabled
 * @property {object} headers - Request headers
 * @property {string} body - Stringified JSON payload
 *
 */

export class Base {
  constructor() {
    if (this.constructor === Base) {
      throw new Error("Cannot instantiate Abstract class Base");
    }
    let host = window.location.origin;

    this.apiBaseURL = process.env.REACT_APP_API_URL || `${host}/api`;
  }

  /**
   * @protected
   *
   * @param {string} route
   * @returns {string} Full API endpoint
   */
  _getFullURL(route) {
    return this.apiBaseURL + route;
  }

  /**
   * @protected
   *
   * @description Generates a request body for making API requests
   * @param {Object} myObj
   * @param {boolean} myObj.authentication
   * @param {?object} myObj.payload
   * @param {string} [myObj.method=GET]
   * @param {string} [myObj.mode=cors]
   * @returns {RequestBody} Request body
   */
  _generateRequestBody({
    authentication = true,
    publicAuthentication = false,
    payload,
    method = "GET",
    mode = "cors",
  }) {
    const reqBody = {
      mode,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    reqBody["method"] = method;

    if (authentication) {
      reqBody["headers"]["Authorization"] = `Bearer ${getAccessToken()}`;
    }
    if (publicAuthentication) {
      reqBody["headers"]["Authorization"] = `D2jFQpFeuJVhud5BWEPfSCdME`;
    }


    if (payload && payload instanceof Object) {
      reqBody["body"] = JSON.stringify(payload);
    }

    return reqBody;
  }

  _secureFetch(url, method, payload) {
    return new Promise((resolve, reject) => {
      fetch(
        url,
        this._generateRequestBody({
          authentication: true,
          payload,
          method,
        })
      )
        .then(async (response) => {
          // response only can be ok in range of 2XX
          if (response.ok) {
            // you can call response.json() here too if you want to return json
            resolve(response);
          } else {
            //handle errors in the way you want to
            if (response.status === 403) {
              toast.error("Your Session Expired Please Login Again");
              localStorage.removeItem("ACCESS_TOKEN");

              setTimeout(() => {
                window.location.href = "/login";
              }, 1000);
            }
            //here you also can thorow custom error too
            const errRes = await response.json();
            if (errRes?.message) {
              toast.error(errRes.message);
            }
            reject(response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  _secureFetchWithTimeout(url, method, payload, timeout) {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      const signal = controller.signal;
      
      // Set up timeout if provided
      let timeoutId;
      if (timeout) {
        timeoutId = setTimeout(() => {
          controller.abort();
        }, timeout);
      }

      fetch(
        url,
        {
          ...this._generateRequestBody({
            authentication: true,
            payload,
            method,
          }),
          signal,
        }
      )
        .then(async (response) => {
          // Clear timeout if request completes successfully
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // response only can be ok in range of 2XX
          if (response.ok) {
            // you can call response.json() here too if you want to return json
            resolve(response);
          } else {
            //handle errors in the way you want to
            if (response.status === 403) {
              toast.error("Your Session Expired Please Login Again");
              localStorage.removeItem("ACCESS_TOKEN");

              setTimeout(() => {
                window.location.href = "/login";
              }, 1000);
            }
            //here you also can thorow custom error too
            const errRes = await response.json();
            if (errRes?.message) {
              toast.error(errRes.message);
            }
            reject(response);
          }
        })
        .catch((error) => {
          // Clear timeout if request completes
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // Handle timeout error specifically
          if (error.name === 'AbortError') {
            reject(new Error(`Request timeout after ${timeout}ms`));
          } else {
            reject(error);
          }
        });
    });
  }
  _secureDownloadFetch(url, method, payload) {
    return new Promise((resolve, reject) => {
      fetch(
        url,
        this._generateRequestBody({
          authentication: true,
          payload,
          method,
        })
      )
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.blob(); // Get the file data as a Blob
          }).then(blob => {
        // ... proceed to trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'docs.pdf'; // Specify the desired filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a); // Clean up the temporary element
        URL.revokeObjectURL(url); // Release the temporary URL
      })
        .catch((error) => {
          reject(error);
        });
    });
  }

  _secureDownloadFetchWithTimeout(url, method, payload, timeout) {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      const signal = controller.signal;
      
      // Set up timeout if provided
      let timeoutId;
      if (timeout) {
        timeoutId = setTimeout(() => {
          controller.abort();
        }, timeout);
      }

      fetch(
        url,
        {
          ...this._generateRequestBody({
            authentication: true,
            payload,
            method,
          }),
          signal,
        }
      )
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.blob(); // Get the file data as a Blob
          }).then(blob => {
            // Clear timeout if request completes successfully
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            
            // ... proceed to trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'docs.pdf'; // Specify the desired filename
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a); // Clean up the temporary element
            URL.revokeObjectURL(url); // Release the temporary URL
          })
        .catch((error) => {
          // Clear timeout if request completes
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          
          // Handle timeout error specifically
          if (error.name === 'AbortError') {
            reject(new Error(`Request timeout after ${timeout}ms`));
          } else {
            reject(error);
          }
        });
    });
  }

  list(obj) {
    throw new Error("Abstract Method has no implementation");
  }

  create(payload) {
    throw new Error("Abstract Method has no implementation");
  }

  get(id) {
    throw new Error("Abstract Method has no implementation");
  }

  update(id, payload) {
    throw new Error("Abstract Method has no implementation");
  }

  delete(id) {
    throw new Error("Abstract Method has no implementation");
  }
}
